import { useTranslation } from "react-i18next";
import type { ReadingComprehensionContent } from "../content-types";
import { cx } from "@/components/ui";
import { Explanation, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** A passage followed by several 4-option comprehension MCQs. Answer maps each
 *  sub-question id → chosen option id; graded when every chosen option is correct. */
export function ReadingComprehensionRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ReadingComprehensionContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  const subs = c.sub_questions ?? [];
  const map = (answer as Record<string, string>) ?? {};

  const pick = (subId: string, optId: string) => {
    if (revealed) return;
    setAnswer({ ...map, [subId]: optId });
  };

  return (
    <div>
      <Prompt>{c.prompt || t("Read the passage and answer")}</Prompt>
      {c.passage && (
        <div className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl bg-black/[0.03] p-3.5 text-sm leading-relaxed text-ink-soft">
          {enableGloss ? (
            <GlossableText text={c.passage} context={c.passage} />
          ) : (
            c.passage
          )}
        </div>
      )}
      <div className="mt-4 space-y-5">
        {subs.map((s) => (
          <div key={s.id}>
            <p className="mb-2 text-sm font-semibold text-ink">
              {enableGloss ? <GlossableText text={s.prompt} context={c.passage} /> : s.prompt}
            </p>
            <div className="space-y-2">
              {s.options.map((o) => {
                const selected = map[s.id] === o.id;
                let state: "neutral" | "selected" | "correct" | "wrong" = selected
                  ? "selected"
                  : "neutral";
                if (revealed) {
                  if (o.is_correct) state = "correct";
                  else if (selected) state = "wrong";
                  else state = "neutral";
                }
                const styles = {
                  neutral: "border-black/10 bg-white hover:border-brand-300",
                  selected: "border-brand-500 bg-brand-50 ring-1 ring-brand-300",
                  correct: "border-emerald-400 bg-emerald-50",
                  wrong: "border-red-400 bg-red-50",
                };
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={revealed}
                    onClick={() => pick(s.id, o.id)}
                    className={cx(
                      "flex w-full items-start gap-3 rounded-xl border px-4 py-2.5 text-left text-sm transition disabled:cursor-default",
                      styles[state],
                    )}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-soft">
                      {o.id}
                    </span>
                    <span className="text-ink">
                      {enableAnswerGloss ? (
                        <GlossableText text={o.text} context={s.prompt} />
                      ) : (
                        o.text
                      )}
                      {revealed && o.rationale && (
                        <span className="mt-0.5 block text-xs text-ink-faint">
                          {enableAnswerGloss ? (
                            <GlossableText text={o.rationale} context={s.prompt} />
                          ) : (
                            o.rationale
                          )}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
